import {Banner} from '@primer/react'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {
  requestCopilotCodingAgentAccess,
  removeCopilotCodingAgentRequest,
  enableCopilotCodingAgentPolicy,
} from '@github-ui/copilot-member-feature-requests'
import {useClickAnalytics} from '@github-ui/use-analytics'
import React, {useCallback, useState} from 'react'
import {buildEnterpriseCopilotCodingAgentPolicyUrl} from '../../utils/constants'

import styles from './MemberRequestBanner.module.css'

export const MemberRequestBanner = React.memo(MemberRequestBannerUnmemoized)

function MemberRequestBannerUnmemoized({
  repo,
  onRepoUpdate,
}: {
  repo: DropdownRepository
  onRepoUpdate: () => Promise<void>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  // Extract org from nameWithOwner (format: "owner/repo")
  const orgLogin = repo.owner

  const sendAgentAnalytics = useCallback(
    (action: string) => {
      sendClickAnalyticsEvent({
        category: 'member_feature_request',
        action: `action.copilot_coding_agent.${action}`,
        label: `ref_cta:${action};ref_loc:agents_new_task_banner;`,
      })
    },
    [sendClickAnalyticsEvent],
  )

  const handleRequestAccess = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await requestCopilotCodingAgentAccess(orgLogin)
      // Request created successfully - refresh the repository data
      await onRepoUpdate()
      sendAgentAnalytics('request')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, sendAgentAnalytics, onRepoUpdate])

  const handleRemoveRequest = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await removeCopilotCodingAgentRequest(orgLogin)
      // Request cancelled successfully - refresh the repository data
      await onRepoUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, onRepoUpdate])

  const handleEnableFeature = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await enableCopilotCodingAgentPolicy(orgLogin)
      // Feature enabled successfully - refresh the repository data
      await onRepoUpdate()
      sendAgentAnalytics('org_enable')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, sendAgentAnalytics, onRepoUpdate])

  let bannerText = ''
  let buttonText = ''
  let onClick = () => {}

  if (repo.userCanRequestCca) {
    bannerText = 'Copilot coding agent access is disabled by your admin.'
    buttonText = isSubmitting ? 'Requesting...' : 'Request access'
    onClick = handleRequestAccess
  } else if (repo.userCanGrantEnterpriseCca) {
    bannerText = 'Copilot coding agent access is disabled.'
    buttonText = 'See Copilot policies'
    onClick = () => {
      sendAgentAnalytics('enterprise_enable')
      const enterpriseSlug = repo.businessSlug || orgLogin
      window.location.href = buildEnterpriseCopilotCodingAgentPolicyUrl(enterpriseSlug)
    }
  } else if (repo.userCanGrantOrgCca) {
    bannerText = 'Copilot coding agent access is disabled.'
    buttonText = isSubmitting ? 'Enabling...' : 'Enable feature'
    onClick = handleEnableFeature
  } else if (repo.userHasRequestedCca) {
    bannerText = 'Copilot coding agent access is disabled by your admin.'
    buttonText = isSubmitting ? 'Removing...' : 'Remove request'
    onClick = handleRemoveRequest
  } else {
    return null
  }

  return (
    <>
      <Banner
        className={styles.requestBanner}
        variant="warning"
        title="Coding Agent Disabled"
        hideTitle
        description={bannerText}
        primaryAction={
          <Banner.PrimaryAction onClick={onClick} disabled={isSubmitting}>
            {buttonText}
          </Banner.PrimaryAction>
        }
      />
      {error && (
        <div aria-live="assertive">
          <Banner variant="critical" title="Error" hideTitle description={error} className={styles.requestBanner} />
        </div>
      )}
    </>
  )
}

try{ MemberRequestBanner.displayName ||= 'MemberRequestBanner' } catch {}
try{ MemberRequestBannerUnmemoized.displayName ||= 'MemberRequestBannerUnmemoized' } catch {}